import {useFormContext} from '@yandex-delivery/form'
import type {Optional} from '@yandex-delivery/types'
import {get, head, isEqual, pipe, some} from '@yandex-delivery/utils'
import {useUpdateEffect} from '@yandex-delivery/utils/hooks'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {OrderEventGoal, useFormUserEventHandler} from 'src/shared/hooks'
import type {FormOrderResponse, MapVerticalRequirements, Order, TariffVertical} from 'src/shared/types'
import {mapVerticalToDeliveryType, toGeoLocation} from 'src/shared/utils'

import {useFetchVerticals} from './use-fetch-verticals'
import {mapVerticalRequirements} from './utils/map-vertical-requirements'
import {mapVerticalsToGroups} from './utils/map-verticals-to-groups'
import {toTariffVerticals} from './utils/to-tariff-verticals'
import {withCorrectDeliveryDirection, withCorrectVertical} from './utils/with-correct-verticals'

const setFieldValueOptions = {
  shouldValidate: false,
  shouldDirty: false,
  shouldTouch: false,
}

export function useVerticals() {
  const prevLocation = useRef<number[]>()
  const previousCity = useRef<string>()
  const previousDeliveryDirection = useRef<string>()

  const {watch, setFieldValue} = useFormContext<Order>()
  const {fetchVerticalsHandler, isVerticalsPending} = useFetchVerticals()
  const handleReachGoal = useFormUserEventHandler()

  const vertical = watch('offerFlow.vertical')
  const verticalName = vertical?.name
  const previousVerticalName = useRef(verticalName)

  const verticalsMap: Optional<MapVerticalRequirements> = watch('__settings.__verticals')
  const sourcePointCoordinates = watch('sourcePoint.address.coordinates')
  const sourcePointCity = watch('sourcePoint.address.city')
  const deliveryDirection = watch('offerFlow.deliveryDirection')
  const deliveryDirectionsVerticals = watch('__settings.__deliveryDirectionsVerticals')
  const verticalGroups = watch('__settings.__deliveryDirectionsVerticals')

  const preparedVerticalGroups = useMemo(() => {
    if (verticalGroups === undefined) {
      return []
    }

    return Object.keys(verticalGroups).map(key => ({
      label: key,
      value: key,
    }))
  }, [verticalGroups])

  const successFetchingHandler = useCallback(
    (result: Optional<FormOrderResponse>) => {
      const groupedVerticals = mapVerticalsToGroups(result)
      const tariffVerticals = toTariffVerticals(result)
      const verticalRequirementsMap = mapVerticalRequirements(tariffVerticals)
      const correctVertical = withCorrectVertical(tariffVerticals, vertical)
      const correctDeliveryDirection = withCorrectDeliveryDirection(groupedVerticals, deliveryDirection)

      setFieldValue('__settings.__verticals', verticalRequirementsMap, setFieldValueOptions)
      setFieldValue('__settings.__deliveryDirectionsVerticals', groupedVerticals, setFieldValueOptions)
      setFieldValue('offerFlow.vertical', correctVertical, setFieldValueOptions)
      setFieldValue('offerFlow.deliveryDirection', correctDeliveryDirection, setFieldValueOptions)
      setFieldValue('deliveryType', mapVerticalToDeliveryType(correctVertical.name), setFieldValueOptions)
    },
    [setFieldValue, deliveryDirection, vertical],
  )

  function handleVerticalDirectionSelect() {
    handleReachGoal(OrderEventGoal.VerticalDestinationClick)
  }

  /**
   * @BusinessLogic:
   * У каждого направления доставки есть своя вертикаль.
   * При смене направления доставки, мы должны убедиться, что выбранная вертикаль включена в выбранное направление,
   * Если нет, нужно установить вертикаль по умолчанию.
   */
  useUpdateEffect(() => {
    if (deliveryDirection === undefined) {
      return
    }

    if (previousDeliveryDirection.current === deliveryDirection) {
      return
    }

    previousDeliveryDirection.current = deliveryDirection

    const deliveryDirectionVerticals: Optional<TariffVertical[]> = get(deliveryDirection, deliveryDirectionsVerticals)

    const isSelectedVerticalsIncluded = pipe(
      deliveryDirectionVerticals,
      some((verticalItem: TariffVertical) => verticalItem.id === verticalName),
    )

    if (isSelectedVerticalsIncluded) {
      return
    }

    const firstDeliveryVerticalId: Optional<string> = pipe(deliveryDirectionVerticals, head, get('id'))

    if (firstDeliveryVerticalId === undefined) {
      return
    }

    const verticalGot = get(firstDeliveryVerticalId, verticalsMap)
    setFieldValue('offerFlow.vertical', verticalGot, setFieldValueOptions)

    if (verticalGot) {
      setFieldValue('deliveryType', mapVerticalToDeliveryType(vertical.name), setFieldValueOptions)
    }
  }, [deliveryDirection, deliveryDirectionsVerticals, verticalsMap])

  /**
   * @BusinessLogic:
   * Если вертикаль не изменилась, то ничего не делаем.
   * Иначе достаем новую вертикаль и устанавливаем ее требования.
   */
  useUpdateEffect(() => {
    if (vertical === undefined) {
      return
    }

    if (previousVerticalName.current === vertical.name) {
      return
    }

    const newVertical = get(vertical.name, verticalsMap)

    if (newVertical === undefined) {
      return
    }

    setFieldValue('deliveryType', mapVerticalToDeliveryType(newVertical.name))
    setFieldValue('offerFlow.vertical.requirements', newVertical.requirements)
    setFieldValue('offerFlow.vertical.dueSettings', newVertical.dueSettings)
    setFieldValue('offerFlow.vertical.deliveryIntervals', newVertical.deliveryIntervals)

    previousVerticalName.current = vertical.name
  }, [setFieldValue, vertical?.name, verticalsMap])

  /**
   * @BusinessLogic:
   * Если есть координаты и они изменились, то делаем запрос на получение вертикалей.
   * Если координаты не изменились, то ничего не делаем.
   */
  useEffect(() => {
    if (sourcePointCoordinates === undefined || sourcePointCity === undefined) {
      return
    }

    if (isEqual(sourcePointCity, previousCity.current) || isEqual(prevLocation.current, sourcePointCoordinates)) {
      return
    }

    fetchVerticalsHandler(toGeoLocation(sourcePointCoordinates), {
      onSuccess: successFetchingHandler,
    })

    prevLocation.current = sourcePointCoordinates
    previousCity.current = sourcePointCity
  }, [fetchVerticalsHandler, sourcePointCity, sourcePointCoordinates, successFetchingHandler])

  return {
    isVerticalsPending,
    preparedVerticalGroups,
    verticalName,
    handleVerticalDirectionSelect,
  } as const
}
